import { useState, useCallback, useReducer } from 'react';
import axios from 'axios';
import { UsageStatistics, AIUsageLog, AdminFilters, AnalyticsData } from '../types/admin';

interface StatisticsState {
  usageStats: UsageStatistics[];
  aiUsageLogs: AIUsageLog[];
  analyticsData: AnalyticsData | null;
  totalCount: number;
  currentPage: number;
  pageSize: number;
  filters: AdminFilters;
  loading: boolean;
  error: string | null;
}

type StatisticsAction =
  | { type: 'FETCH_START' }
  | { type: 'FETCH_USAGE_SUCCESS'; payload: { data: UsageStatistics[] } }
  | { type: 'FETCH_ANALYTICS_SUCCESS'; payload: { data: AnalyticsData } }
  | { type: 'FETCH_LOGS_SUCCESS'; payload: { data: AIUsageLog[]; totalCount: number } }
  | { type: 'FETCH_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_FILTERS'; payload: AdminFilters }
  | { type: 'SET_PAGE'; payload: number };

const initialState: StatisticsState = {
  usageStats: [],
  aiUsageLogs: [],
  analyticsData: null,
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  filters: {},
  loading: false,
  error: null,
};

function statisticsReducer(
  state: StatisticsState,
  action: StatisticsAction
): StatisticsState {
  switch (action.type) {
    case 'FETCH_START':
      return { ...state, loading: true, error: null };
    case 'FETCH_USAGE_SUCCESS':
      return { ...state, usageStats: action.payload.data, loading: false };
    case 'FETCH_ANALYTICS_SUCCESS':
      return { ...state, analyticsData: action.payload.data, loading: false };
    case 'FETCH_LOGS_SUCCESS':
      return {
        ...state,
        aiUsageLogs: action.payload.data,
        totalCount: action.payload.totalCount,
        loading: false,
      };
    case 'FETCH_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'SET_FILTERS':
      return { ...state, filters: action.payload, currentPage: 1 };
    case 'SET_PAGE':
      return { ...state, currentPage: action.payload };
    default:
      return state;
  }
}

export const useStatistics = () => {
  const [state, dispatch] = useReducer(statisticsReducer, initialState);

  const fetchAnalyticsData = useCallback(async (period: '7d' | '30d' | '90d' = '30d', userId?: number) => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await axios.get(`/api/admin/statistics/usage?period=${period}${userId ? `&userId=${userId}` : ''}`);
      const analyticsData = response.data.analytics;
      
      if (analyticsData) {
        dispatch({ 
          type: 'FETCH_ANALYTICS_SUCCESS', 
          payload: { 
            data: analyticsData 
          } 
        });
      } else {
        throw new Error('Analytics data not found in response');
      }
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || 'アナリティクスデータの取得に失敗しました'
        : 'アナリティクスデータの取得に失敗しました';
      dispatch({ type: 'FETCH_ERROR', payload: errorMessage });
    }
  }, []);

  const fetchUsageStatistics = useCallback(async (period: '7d' | '30d' | '90d' = '30d', userId?: number) => {
    dispatch({ type: 'FETCH_START' });
    try {
      const response = await axios.get(`/api/admin/statistics/usage?period=${period}${userId ? `&userId=${userId}` : ''}`);
      dispatch({ 
        type: 'FETCH_USAGE_SUCCESS', 
        payload: { 
          data: response.data.data || response.data 
        } 
      });
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || '利用統計の取得に失敗しました'
        : '利用統計の取得に失敗しました';
      dispatch({ type: 'FETCH_ERROR', payload: errorMessage });
    }
  }, []);

  const fetchAIUsageLogs = useCallback(async () => {
    dispatch({ type: 'FETCH_START' });
    try {
      const params = new URLSearchParams({
        page: state.currentPage.toString(),
        limit: state.pageSize.toString(),
        ...state.filters.userSearch && { userSearch: state.filters.userSearch },
        ...state.filters.expertAI && { expertAI: state.filters.expertAI },
        ...state.filters.dateRange?.start && { startDate: state.filters.dateRange.start },
        ...state.filters.dateRange?.end && { endDate: state.filters.dateRange.end },
      });

      const response = await axios.get(`/api/admin/ai-usage-logs?${params}`);
      dispatch({
        type: 'FETCH_LOGS_SUCCESS',
        payload: {
          data: response.data.logs,
          totalCount: response.data.totalCount,
        },
      });
    } catch (error) {
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.message || 'AI利用ログの取得に失敗しました'
        : 'AI利用ログの取得に失敗しました';
      dispatch({ type: 'FETCH_ERROR', payload: errorMessage });
    }
  }, [state.currentPage, state.pageSize, state.filters]);

  const setPage = useCallback((page: number) => {
    dispatch({ type: 'SET_PAGE', payload: page });
  }, []);

  const setFilters = useCallback((filters: AdminFilters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  return {
    ...state,
    fetchUsageStatistics,
    fetchAnalyticsData,
    fetchAIUsageLogs,
    setPage,
    setFilters,
    clearError,
  };
};